/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import {
  BUTTON_FORMAT_SOFT,
  BUTTON_FORMAT_STRONG,
  BUTTON_THEME_DEFAULT,
  BUTTON_THEME_PRIMARY,
  Button,
  CheckboxRadioProps,
  DialogContentProps,
  FormControlOption,
  RadioButtons,
  Toggle,
  ToggleProps,
} from '@squiz/sds';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccessControlList, AccessControlListValues, useFileStore } from '../../../../../../../../Services/file-store';
import { FILE_EXPLORER_STRINGS } from '../../../../FileExplorer.constants';
import { useFileExplorerStore } from '../../../../FileExplorer.state';
import { PermissionsDialogContentProps } from './PermissionsDialogContent.props';

export const PermissionsDialogContent = (props: DialogContentProps): JSX.Element => {
  const { t } = useTranslation();

  const { close, setButtonKeys, setButtons } = props;

  const itemProps = props.customProps as PermissionsDialogContentProps;
  const { id, type } = itemProps;

  const labels = useMemo(() => {
    return {
      applyChanges: t(FILE_EXPLORER_STRINGS.applyChanges),
      cancel: t(FILE_EXPLORER_STRINGS.cancel),
      cascadePermission: t(FILE_EXPLORER_STRINGS.cascadePermission),
      changeSelectedPermissions: t(FILE_EXPLORER_STRINGS.changeSelectedPermissions),
      changeSinglePermission: t(FILE_EXPLORER_STRINGS.changeSinglePermission),
      itemsSetToPrivate: t(FILE_EXPLORER_STRINGS.itemsSetToPrivate),
      itemsSetToPublic: t(FILE_EXPLORER_STRINGS.itemsSetToPublic),
      mixedPermissionsWarning: t(FILE_EXPLORER_STRINGS.mixedPermissionsWarning),
      permissions: t(FILE_EXPLORER_STRINGS.permissions),
      private: t(FILE_EXPLORER_STRINGS.private),
      privatePermissionDescription: t(FILE_EXPLORER_STRINGS.privatePermissionDescription),
      public: t(FILE_EXPLORER_STRINGS.public),
      publicPermissionDescription: t(FILE_EXPLORER_STRINGS.publicPermissionDescription),
      singlePrivatePermissionDescription: t(FILE_EXPLORER_STRINGS.singleFilePrivatePermissionDescription),
      singlePublicPermissionDescription: t(FILE_EXPLORER_STRINGS.singleFilePublicPermissionDescription),
    };
  }, [t]);

  const { loadingState, changePermissions, getDirectoryContents } = useFileStore();
  const { selectedRows, fileExplorerPosition, setDirectoryContents, directoryContents } = useFileExplorerStore();

  const dialogSetup = useMemo((): {
    defaultSelection: AccessControlList | undefined;
    countPrivate?: number;
    countPublic?: number;
    renderList: boolean;
    showCascadeToggle: boolean;
  } => {
    const countPublic = selectedRows.filter((row) => row.visibility === AccessControlListValues.PUBLIC_READ).length;
    const countPrivate = selectedRows.filter((row) => row.visibility === AccessControlListValues.PRIVATE).length;
    const mixedPermissions = !!(countPublic && countPrivate);
    const defaultSelection = mixedPermissions
      ? undefined
      : countPublic
        ? AccessControlListValues.PRIVATE
        : AccessControlListValues.PUBLIC_READ;

    const countDirectory = selectedRows.filter((row) => row.type === 'DIRECTORY').length;
    const showCascadeToggle = countDirectory > 0;

    // Omit changing permissions for row selections - only show the dialog for the row used via an action menu
    if (id) {
      const item = directoryContents.find((file) => file.id === id);
      if (item) {
        return {
          defaultSelection: item.access,
          renderList: false,
          showCascadeToggle: type === 'DIRECTORY',
        };
      }
    }

    return {
      countPrivate,
      countPublic,
      defaultSelection,
      renderList: mixedPermissions,
      showCascadeToggle,
    };
  }, [directoryContents, id, selectedRows, type]);

  const [checkboxSelection, setCheckboxSelection] = useState<AccessControlList | undefined>(
    dialogSetup.defaultSelection,
  );
  const [cascadeAccess, setCascadeAccess] = useState<boolean | undefined>();

  useEffect(() => {
    setCheckboxSelection(dialogSetup.defaultSelection);
  }, [dialogSetup.defaultSelection, selectedRows]);

  const handlePermissionChange = useCallback(async () => {
    if (!checkboxSelection) {
      return;
    }

    const singleItem = id &&
      type && {
        id,
        type,
      };
    const selectedItems = selectedRows.map((row) => ({ id: row.id, type: row.type }));

    const items = singleItem ? [singleItem] : selectedItems;

    await changePermissions({
      access: checkboxSelection as AccessControlList,
      cascadeAccess,
      items,
    });

    const fileList = await getDirectoryContents({ source: fileExplorerPosition });
    setDirectoryContents(fileList.data);

    close();
  }, [
    cascadeAccess,
    changePermissions,
    checkboxSelection,
    close,
    fileExplorerPosition,
    getDirectoryContents,
    id,
    selectedRows,
    setDirectoryContents,
    type,
  ]);

  useEffect(() => {
    const cancelButton = (
      <Button
        classes="file-explorer__chmod-dialog-btn--cancel"
        format={BUTTON_FORMAT_SOFT}
        onClick={(): void => close()}
        text={labels.cancel}
        theme={BUTTON_THEME_DEFAULT}
      />
    );

    const applyButton = (
      <Button
        classes="file-explorer__chmod-dialog-btn--apply"
        format={BUTTON_FORMAT_STRONG}
        isLoading={loadingState.changePermissionsLoading}
        onClick={async (): Promise<void> => handlePermissionChange()}
        text={labels.applyChanges}
        theme={BUTTON_THEME_PRIMARY}
      />
    );

    setButtonKeys([`cancel`, `apply`]);
    setButtons([cancelButton, applyButton]);
  }, [close, handlePermissionChange, labels, loadingState, setButtonKeys, setButtons]);

  const accessRadioRef = useRef<HTMLInputElement>(null);
  const accessRadioProps: CheckboxRadioProps = {
    classes: 'file-explorer__dialog-radio-buttons',
    fieldLabel: labels.permissions,
    hideLabel: true,
    id: labels.permissions,
    name: labels.permissions,
    options: [
      {
        checked: checkboxSelection === AccessControlListValues.PUBLIC_READ,
        extraContent: (
          <p className="radio-buttons__extra-content">
            {id ? labels.singlePublicPermissionDescription : labels.publicPermissionDescription}
          </p>
        ),
        key: labels.public,
        label: labels.public,
        value: AccessControlListValues.PUBLIC_READ,
      },
      {
        checked: checkboxSelection === AccessControlListValues.PRIVATE,
        extraContent: (
          <p className="radio-buttons__extra-content">
            {id ? labels.singlePrivatePermissionDescription : labels.privatePermissionDescription}
          </p>
        ),
        key: labels.private,
        label: labels.private,
        value: AccessControlListValues.PRIVATE,
      },
    ],
    radioCallback: (option: FormControlOption) => {
      setCheckboxSelection(option.value as AccessControlList);
    },
  };

  const toggleCascadeRef = useRef<HTMLButtonElement>(null);
  const toggleCascadeProps: ToggleProps = {
    callback: (checked: boolean) => setCascadeAccess(checked),
    checked: cascadeAccess,
    classes: 'file-explorer__chmod-dialog-toggle--cascade',
    fieldLabel: labels.cascadePermission,
    id: 'cascade-access',
    name: 'cascade-access',
  };

  return (
    <div className="file-explorer__dialog-content change-permissions">
      <div className="sds-dialog__heading custom">
        <span>{labels.permissions}</span>
      </div>
      {dialogSetup.renderList && (
        <div className="file-explorer__dialog-content-list">
          <p>{labels.mixedPermissionsWarning}</p>
          <ul className="file-explorer__dialog-content-list-items">
            <li>
              {dialogSetup.countPublic} {labels.itemsSetToPublic}
            </li>
            <li>
              {dialogSetup.countPrivate} {labels.itemsSetToPrivate}
            </li>
          </ul>
        </div>
      )}
      <p className="file-explorer__dialog-text">
        {id ? labels.changeSinglePermission : labels.changeSelectedPermissions}
      </p>
      <div className="file-explorer__dialog-radio-buttons">
        <RadioButtons {...accessRadioProps} ref={accessRadioRef} />
      </div>
      {dialogSetup.showCascadeToggle && (
        <div className="file-explorer__dialog-toggle">
          <Toggle {...toggleCascadeProps} ref={toggleCascadeRef} />
        </div>
      )}
    </div>
  );
};
